import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { KNOWN_PERMISSION_VALUES } from '@/hooks/permissionConstants';

interface TokenClaims {
  roles?: string[];
  scp?: string | string[];
  scope?: string;
  [key: string]: unknown;
}

export interface UseTokenRolesReturn {
  roles: string[];
  permissions: string[];
  memberIdentifiers: string[];
  isLoading: boolean;
  error: string | null;
}

interface DecodedTokenData {
  roles: string[];
  permissions: string[];
  memberIdentifiers: string[];
}

const KNOWN_PERMISSION_SET = new Set(KNOWN_PERMISSION_VALUES);

const uniqueValues = (values: string[]): string[] => [...new Set(values.filter(Boolean))];

const extractScopes = (payload: TokenClaims): string[] => {
  if (Array.isArray(payload.scp)) {
    return payload.scp.filter((value): value is string => typeof value === 'string');
  }

  if (typeof payload.scp === 'string') {
    return payload.scp.split(' ').filter(Boolean);
  }

  if (typeof payload.scope === 'string') {
    return payload.scope.split(' ').filter(Boolean);
  }

  return [];
};

export const useTokenRoles = (): UseTokenRolesReturn => {
  const { accessToken } = useAuth();
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [memberIdentifiers, setMemberIdentifiers] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decodeTokenData = (token: string): DecodedTokenData => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      const payload: TokenClaims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

      const decodedRoles = Array.isArray(payload.roles)
        ? payload.roles.filter((value): value is string => typeof value === 'string')
        : [];
      const decodedScopes = extractScopes(payload);
      const classifiedPermissions = uniqueValues(
        [...decodedRoles, ...decodedScopes].filter((value) => KNOWN_PERMISSION_SET.has(value))
      );
      const classifiedMembers = uniqueValues(
        decodedRoles.filter((value) => !KNOWN_PERMISSION_SET.has(value))
      );

      return {
        roles: decodedRoles,
        permissions: classifiedPermissions,
        memberIdentifiers: classifiedMembers,
      };
    } catch (err: unknown) {
      console.error('Error decoding JWT for token permissions and member identifiers:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (accessToken) {
      setIsLoading(true);
      setError(null);
      
      try {
        const tokenData = decodeTokenData(accessToken);
        setRoles(tokenData.roles);
        setPermissions(tokenData.permissions);
        setMemberIdentifiers(tokenData.memberIdentifiers);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to decode token';
        setError(errorMessage);
        setRoles([]);
        setPermissions([]);
        setMemberIdentifiers([]);
      } finally {
        setIsLoading(false);
      }
    } else {
      setRoles([]);
      setPermissions([]);
      setMemberIdentifiers([]);
      setError(null);
      setIsLoading(false);
    }
  }, [accessToken]);

  return {
    roles,
    permissions,
    memberIdentifiers,
    isLoading,
    error,
  };
};